// A simple articulated body: enough joints to produce all 33 MediaPipe pose
// landmarks, with key poses for lying prone, the press-up and the surf stance.

import { toeSideSign } from '../core/mapping';
import { LM, NUM_LANDMARKS } from '../core/pose';
import {
  add3, cross3, dot3, len3, lerp3, mul3, norm3, sub3, v3,
  type Projected, type SimWorld, type Vec3, type FootPose2D,
} from './world';

export interface FootPoints {
  heel: Vec3;
  toe: Vec3;
  ankle: Vec3;
}

export interface Body {
  head: Vec3;
  /** Unit vectors: where the face points, and the top of the head. */
  fwd: Vec3;
  up: Vec3;
  lShoulder: Vec3; rShoulder: Vec3;
  lElbow: Vec3; rElbow: Vec3;
  lWrist: Vec3; rWrist: Vec3;
  lHip: Vec3; rHip: Vec3;
  lKnee: Vec3; rKnee: Vec3;
  lFoot: FootPoints;
  rFoot: FootPoints;
}

const smooth = (t: number) => {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
};
export { smooth as smoothstep };

/** Foot landmarks for a foot resting flat on the floor (optionally lifted). */
export function footPoints(p: FootPose2D, lift = 0): FootPoints {
  const d = v3(Math.cos(p.angle), Math.sin(p.angle), 0);
  const c = v3(p.centre.x, p.centre.y, 0);
  return {
    heel: add3(add3(c, mul3(d, -11.5)), v3(0, 0, 3.5 + lift)),
    toe: add3(add3(c, mul3(d, 12)), v3(0, 0, 1.5 + lift)),
    ankle: add3(add3(c, mul3(d, -5.5)), v3(0, 0, 9 + lift)),
  };
}

/**
 * Lying face down along the board, toes just off the tail, hands flat beside
 * the lower ribs ready to press up (≈46 cm apart, as measured in surfers'
 * pop-ups). lift 0→1 = chest on the board → arms extended.
 */
export function pronePose(w: SimWorld, lift: number): Body {
  const yT = w.tailY;
  const side = (x: number) => ({ l: -x, r: x }); // prone, the rider's left is −x
  const foot = (x: number): FootPoints => ({
    toe: v3(x, yT, 2),
    heel: v3(x, yT + 5, 19),
    ankle: v3(x, yT + 9, 13),
  });
  const sh = side(19);
  const hip = side(13);
  const kn = side(10);
  const wr = side(23);
  const zS = 20 + 32 * lift;
  // Elbows tucked and pointing back while the chest is down, straight under the shoulders at the top.
  const elbow = (x: number) => lerp3(v3(x * 1.25, yT + 124, 15), v3(x * 1.05, yT + 124, 34), lift);
  return {
    head: v3(0, yT + 168, 24 + 34 * lift),
    fwd: norm3(v3(0, 0.55, -0.8)),
    up: norm3(v3(0, 0.8, 0.55)),
    lShoulder: v3(sh.l, yT + 140, zS),
    rShoulder: v3(sh.r, yT + 140, zS),
    lElbow: elbow(-22),
    rElbow: elbow(22),
    lWrist: v3(wr.l, yT + 108, 3),
    rWrist: v3(wr.r, yT + 108, 3),
    lHip: v3(hip.l, yT + 90, 15 + 16 * lift),
    rHip: v3(hip.r, yT + 90, 15 + 16 * lift),
    lKnee: v3(kn.l, yT + 47, 9 + 5 * lift),
    rKnee: v3(kn.r, yT + 47, 9 + 5 * lift),
    lFoot: foot(-9),
    rFoot: foot(9),
  };
}

const THIGH = 44;
const SHIN = 43;

/**
 * Two-bone IK: the knee position for a leg of the given segment lengths
 * between `hip` and `ankle`, bending toward `pole`.
 */
function solveKnee(hip: Vec3, ankle: Vec3, pole: Vec3): Vec3 {
  const d = sub3(ankle, hip);
  const dist = Math.min(THIGH + SHIN - 0.01, Math.max(1, len3(d)));
  const dir = norm3(d);
  const along = (THIGH * THIGH - SHIN * SHIN + dist * dist) / (2 * dist);
  const out = Math.sqrt(Math.max(0, THIGH * THIGH - along * along));
  const perp = norm3(sub3(pole, mul3(dir, dot3(pole, dir))));
  return add3(hip, add3(mul3(dir, along), mul3(perp, out)));
}

/** Hip-to-ankle distance that gives a knee flexion of `flexDeg` (0 = straight). */
const legSpan = (flexDeg: number) => {
  const interior = Math.PI - (flexDeg * Math.PI) / 180;
  return Math.sqrt(THIGH * THIGH + SHIN * SHIN - 2 * THIGH * SHIN * Math.cos(interior));
};

/**
 * The functional surf stance (see docs/STANCE.md), built around the given
 * feet. Knees are bent (≈70° in a full crouch, within the 30–80° semi-squat
 * the literature describes) and angled in toward each other: the front knee
 * over the front foot, the back knee driven forward and in toward the front
 * leg — never splayed apart. Hips sit low and centred over the stringer; the
 * bend comes from the knees, not the waist. The back stays fairly straight
 * with a slight forward lean, chest open toward the nose, one arm over each
 * rail with elbows bent, eyes ahead. `crouch` 0..1 scales the knee bend;
 * `sway` is a small horizontal drift of the upper body (cm).
 */
export function stancePose(w: SimWorld, front: FootPoints, rear: FootPoints, crouch = 1, sway = { x: 0, y: 0 }): Body {
  const s = toeSideSign(w.stance);
  const up = v3(0, 0, 1);
  const toeSide = v3(s, 0, 0);
  const nose = v3(0, 1, 0);
  const deg = (d: number) => (d * Math.PI) / 180;
  const facing = (yawDeg: number) => norm3(add3(mul3(toeSide, Math.cos(deg(yawDeg))), mul3(nose, Math.sin(deg(yawDeg)))));
  const rightOf = (f: Vec3) => norm3(cross3(f, up)); // the rider's right when facing f
  const frontIsLeft = s === 1;
  const [lFoot, rFoot] = frontIsLeft ? [front, rear] : [rear, front];

  // Pelvis: centred between the feet, over the stringer and sitting back a
  // touch toward the heels (as in a squat), hips partly open to the nose.
  const hipRight = rightOf(facing(30));
  const mid = lerp3(front.ankle, rear.ankle, 0.5);
  const base = v3(mid.x * 0.3 - 4 * s + sway.x, mid.y + 1 + sway.y, 0);
  const frontHipOffset = mul3(hipRight, frontIsLeft ? -12 : 12);
  const rearHipOffset = mul3(hipRight, frontIsLeft ? 12 : -12);
  // Lower the pelvis until the front knee reaches the target flexion.
  const flex = 8 + 62 * crouch;
  const frontHipXY = add3(base, frontHipOffset);
  const horiz = Math.hypot(frontHipXY.x - front.ankle.x, frontHipXY.y - front.ankle.y);
  const span = legSpan(flex);
  const hipZ = front.ankle.z + Math.sqrt(Math.max(0, span * span - horiz * horiz));
  const hipC = add3(base, v3(0, 0, hipZ));
  const frontHip = add3(hipC, frontHipOffset);
  const rearHip = add3(hipC, rearHipOffset);

  // Knees: front over the front foot (slightly in); back forward and in toward the front leg.
  const frontPole = norm3(add3(mul3(toeSide, 0.8), mul3(nose, 0.15)));
  const rearPole = norm3(add3(mul3(nose, 0.75), mul3(toeSide, 0.55)));
  const frontKnee = solveKnee(frontHip, front.ankle, frontPole);
  const rearKnee = solveKnee(rearHip, rear.ankle, rearPole);

  // Torso: fairly upright, slight forward lean; chest opens further to the nose.
  const chestFacing = facing(52);
  const chestRight = rightOf(chestFacing);
  const lean = deg(16 * crouch + 4);
  const shC = add3(hipC, add3(mul3(up, 48 * Math.cos(lean)), add3(mul3(chestFacing, 48 * Math.sin(lean)), v3(sway.x * 0.5, sway.y * 0.5, 0))));
  const lSh = add3(shC, mul3(chestRight, -18));
  const rSh = add3(shC, mul3(chestRight, 18));
  const head = add3(shC, add3(mul3(up, 24), mul3(chestFacing, 4)));
  // Eyes ahead toward the nose / down the line, slightly down.
  const fwd = norm3(add3(add3(mul3(nose, 0.85), mul3(toeSide, 0.3)), mul3(up, -0.25)));
  const headUp = norm3(sub3(up, mul3(fwd, fwd.z)));

  // Arms: each over its own rail, hands about waist height, elbows bent.
  const arm = (sh: Vec3) => {
    const rail = Math.sign(sh.x - shC.x) || 1;
    const wrist = v3(rail * 34, hipC.y + (rail === s ? 12 : 24), hipC.z + 4);
    const elbow = add3(lerp3(sh, wrist, 0.5), add3(v3(rail * 9, 0, 0), mul3(up, -7)));
    return { e: elbow, w: wrist };
  };
  const lArm = arm(lSh);
  const rArm = arm(rSh);
  return {
    head, fwd, up: headUp,
    lShoulder: lSh, rShoulder: rSh,
    lElbow: lArm.e, rElbow: rArm.e,
    lWrist: lArm.w, rWrist: rArm.w,
    lHip: frontIsLeft ? frontHip : rearHip,
    rHip: frontIsLeft ? rearHip : frontHip,
    lKnee: frontIsLeft ? frontKnee : rearKnee,
    rKnee: frontIsLeft ? rearKnee : frontKnee,
    lFoot, rFoot,
  };
}

/** Standing beside the board, side-on, for "step off" moments. */
export function standingAside(w: SimWorld, offsetX: number): Body {
  const s = toeSideSign(w.stance);
  const front = footPoints({ centre: { x: offsetX, y: w.tape.gap * 0.5 + 18 }, angle: Math.atan2(0, s) });
  const rear = footPoints({ centre: { x: offsetX, y: w.tape.gap * 0.5 - 18 }, angle: Math.atan2(0, s) });
  return stancePose(w, front, rear, 0);
}

const lerpFoot = (a: FootPoints, b: FootPoints, t: number): FootPoints => ({
  heel: lerp3(a.heel, b.heel, t),
  toe: lerp3(a.toe, b.toe, t),
  ankle: lerp3(a.ankle, b.ankle, t),
});

export function blendBodies(a: Body, b: Body, t: number): Body {
  if (t <= 0) return a;
  if (t >= 1) return b;
  const out = {} as Body;
  for (const k of Object.keys(a) as (keyof Body)[]) {
    const va = a[k];
    const vb = b[k];
    if ('heel' in va) (out as unknown as Record<string, FootPoints>)[k] = lerpFoot(va, vb as FootPoints, t);
    else (out as unknown as Record<string, Vec3>)[k] = lerp3(va, vb as Vec3, t);
  }
  out.fwd = norm3(out.fwd);
  out.up = norm3(out.up);
  return out;
}

/** Foot moving between two rest poses along a lifted arc. */
export function swingFoot(a: FootPoints, b: FootPoints, t: number, arc: number): FootPoints {
  const e = smooth(t);
  const lift = v3(0, 0, Math.sin(Math.PI * Math.min(1, Math.max(0, t))) * arc);
  const p = lerpFoot(a, b, e);
  return { heel: add3(p.heel, lift), toe: add3(p.toe, lift), ankle: add3(p.ankle, lift) };
}

export interface Landmark3 {
  p: Vec3;
  vis: number;
}

/** All 33 landmarks in world space. */
export function bodyLandmarks(b: Body): Vec3[] {
  const left = norm3(cross3(b.up, b.fwd));
  const at = (base: Vec3, ...terms: [Vec3, number][]) => terms.reduce((acc, [v, k]) => add3(acc, mul3(v, k)), base);
  const out: Vec3[] = new Array(NUM_LANDMARKS);
  const face = (i: number, fwd: number, up: number, side: number) => (out[i] = at(b.head, [b.fwd, fwd], [b.up, up], [left, side]));
  face(LM.nose, 10, -1, 0);
  face(LM.leftEyeInner, 8, 3, 1.5);
  face(LM.leftEye, 7.5, 3.2, 3.2);
  face(LM.leftEyeOuter, 6.5, 3.2, 4.8);
  face(LM.rightEyeInner, 8, 3, -1.5);
  face(LM.rightEye, 7.5, 3.2, -3.2);
  face(LM.rightEyeOuter, 6.5, 3.2, -4.8);
  face(LM.leftEar, -1, 1, 7.5);
  face(LM.rightEar, -1, 1, -7.5);
  face(LM.mouthLeft, 8.5, -4, 2.5);
  face(LM.mouthRight, 8.5, -4, -2.5);
  out[LM.leftShoulder] = b.lShoulder;
  out[LM.rightShoulder] = b.rShoulder;
  out[LM.leftElbow] = b.lElbow;
  out[LM.rightElbow] = b.rElbow;
  out[LM.leftWrist] = b.lWrist;
  out[LM.rightWrist] = b.rWrist;
  const hand = (wrist: Vec3, elbow: Vec3, idx: { pinky: number; index: number; thumb: number }, sideSign: number) => {
    const d = norm3(sub3(wrist, elbow));
    const side = norm3(cross3(d, v3(0, 0, 1)));
    out[idx.index] = at(wrist, [d, 9], [side, 1.5 * sideSign]);
    out[idx.pinky] = at(wrist, [d, 8], [side, -3 * sideSign]);
    out[idx.thumb] = at(wrist, [d, 4], [side, 4 * sideSign]);
  };
  hand(b.lWrist, b.lElbow, { pinky: LM.leftPinky, index: LM.leftIndex, thumb: LM.leftThumb }, 1);
  hand(b.rWrist, b.rElbow, { pinky: LM.rightPinky, index: LM.rightIndex, thumb: LM.rightThumb }, -1);
  out[LM.leftHip] = b.lHip;
  out[LM.rightHip] = b.rHip;
  out[LM.leftKnee] = b.lKnee;
  out[LM.rightKnee] = b.rKnee;
  out[LM.leftAnkle] = b.lFoot.ankle;
  out[LM.rightAnkle] = b.rFoot.ankle;
  out[LM.leftHeel] = b.lFoot.heel;
  out[LM.rightHeel] = b.rFoot.heel;
  out[LM.leftToe] = b.lFoot.toe;
  out[LM.rightToe] = b.rFoot.toe;
  return out;
}

export type Projector = (p: Vec3) => Projected;
